import React from "react";
import { useSelector } from "react-redux";
import { getUrlOfPaymentIframe } from "@qgiv/core-react";
import { reduxRegistrationsHasCommitments } from "@qgiv/p2p-registration";
import { ThemeType } from "../../../redux/constants";
import {
    selectFormDataValues,
    selectSessionReduxData,
} from "../../../redux/slices/formDataSlice";
import {
    selectCurrentPage,
    updateCurrentPage,
} from "../../../redux/slices/currentPageSlice";
import {
    selectErroredEntities,
    setErroredEntities,
    setOutdated,
    setUpdated,
} from "../../../redux/slices/raceConditionsSlice";
import {
    selectAllRegistration,
    selectRegistrationTotals,
    updatePayment,
} from "../../../redux/slices/registrationSlice";
import {
    selectHasParentRegistration,
    setAppHasSubmittedRegistration,
} from "../../../redux/slices/appSettingsSlice";
import { selectParentRegistration } from "../../../redux/slices/parentRegistrationSlice";
import { selectAllClassifications } from "../../../redux/slices/classificationsSlice";
import { selectThemeSettings } from "../../../redux/slices/cmsSettingsSlice";
import { selectConfig } from "../../../redux/slices/configSlice";
import { selectAllFormSettings } from "../../../redux/slices/formSettingsSlice";
import { selectAllCategories } from "../../../redux/slices/categoriesSlice";
import {
    selectAllP2pSettings,
    selectBaseUrl,
} from "../../../redux/slices/p2pSettingsSlice";
import { selectAllProductSettings } from "../../../redux/slices/productSettingsSlice";
import { selectAllProducts } from "../../../redux/slices/productsSlice";
import { fetchReceipt } from "../../../redux/slices/receiptSlice";
import { updateRegistrationAccount } from "../../../redux/slices/registrationAccountSlice";
// eslint-disable-next-line import/no-cycle
import PaymentPage from "./PaymentPage";

const ConnectedPaymentPage = () => {
    const formDataValues = useSelector(selectFormDataValues);
    const sessionStorageReduxData = useSelector(selectSessionReduxData);
    const currentPage = useSelector(selectCurrentPage);
    const erroredEntities = useSelector(selectErroredEntities);
    const {
        classification = {},
        registrations = [{}],
        payment = {},
        promoDiscounts = {},
        promoCodes = [],
        storeCart = [{}],
    } = useSelector(selectAllRegistration);
    const hasParentRegistration = useSelector(selectHasParentRegistration);
    const parentRegistration = useSelector(selectParentRegistration);
    const totals = useSelector(selectRegistrationTotals);
    const { shippingMethodAmount = 0, total = 0 } = totals;
    const registrationHasCommitment =
        reduxRegistrationsHasCommitments(registrations);
    // If a registration is missing Needs_To_Be_Reviewed flag
    // set as true since existing registrations and outdated actions
    // set the flag as false.
    const hasUnconfirmedRegistrations = registrations
        .map(({ Needs_To_Be_Reviewed = false }) => Needs_To_Be_Reviewed)
        .some((bool) => bool === true);
    const { transaction = {}, participants = [] } = promoDiscounts;
    // Registration level stored classification
    const { Classification = [] } = classification;
    const classifications = useSelector(selectAllClassifications);
    const config = useSelector(selectConfig);
    const { QGIV_HOST, isCms } = config;
    const formSettings = useSelector(selectAllFormSettings);
    const { id, title, isStandardDonationForm } = formSettings;
    const categories = useSelector(selectAllCategories);
    const p2pSettings = useSelector(selectAllP2pSettings);
    const { enableClassifications, matchingRatio } = p2pSettings;
    const productSettings = useSelector(selectAllProductSettings);
    const { offerShipping, shipping_methods, requirePhone } = productSettings;
    const products = useSelector(selectAllProducts);
    const settings = {
        categories,
        formSettings,
        p2pSettings,
        productSettings,
        products,
    };
    const themeSettings = useSelector((state) =>
        selectThemeSettings(ThemeType.HEADER, state),
    );
    const {
        primaryRegProgressBar: { checkoutStepLabel = "Finish Up" },
    } = themeSettings;

    const baseUrl = useSelector(selectBaseUrl);

    const redirectToClassificationPage =
        enableClassifications &&
        classifications?.length > 0 &&
        Classification.length < 1;

    const redirectToRegistrationPage =
        registrations.length === 1 &&
        Object.keys(registrations[0]).length === 0;

    const registrationRedirect = redirectToRegistrationPage
        ? `${baseUrl}/form/registration`
        : undefined;

    const redirect = redirectToClassificationPage
        ? `${baseUrl}/form/classification`
        : registrationRedirect;

    const validateShippingDetails = storeCart.length > 0 && offerShipping;
    const validateShippingMethods =
        validateShippingDetails && shipping_methods.length > 0;

    const validationSettings = {
        isStandardDonationForm,
        matchingRatio,
        registrationHasCommitment,
        total,
        shippingPhoneRequired: requirePhone,
        validateShippingDetails,
        validateShippingMethods,
    };
    const documentTitle = `${title} | ${checkoutStepLabel}`;

    const parentRegistrationId = hasParentRegistration
        ? parentRegistration.registrationId
        : "0";

    const formId = id;
    const originOfPaymentIframe = QGIV_HOST;
    const urlOfPaymentIframe = getUrlOfPaymentIframe({
        formId,
        isCms,
        originOfPaymentIframe,
    });

    const paymentPageProps = {
        baseUrl,
        classification,
        currentPage,
        discounts: { transaction, participants },
        erroredEntities,
        hasUnconfirmedRegistrations,
        payment,
        formDataValues,
        promoCodes,
        redirect,
        registrations,
        registrationHasCommitment,
        settings,
        storeCart,
        validationSettings,
        documentTitle,
        parentRegistrationId,
        shippingMethodAmount,
        totals,
        sessionStorageReduxData,
        // Actions
        fetchReceipt,
        setAppHasSubmittedRegistration,
        setErroredEntities,
        setOutdated,
        setUpdated,
        updateRegistrationAccount,
        updateCurrentPage,
        updatePayment,
        urlOfPaymentIframe,
    };
    return <PaymentPage {...paymentPageProps} />;
};

export default ConnectedPaymentPage;
